import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider }      from './context/AuthContext';
import { CartProvider }      from './context/CartContext';
import { StockAuthProvider } from './context/StockAuthContext';
import { StockProvider }     from './context/StockContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';

// LOMED Client
import Home          from './pages/client/Home';
import Catalogue     from './pages/client/Catalogue';
import Reservation   from './pages/client/Reservation';
import About         from './pages/client/About';
import Contact       from './pages/client/Contact';
import MesCommandes  from './pages/client/MesCommandes';
import Devis         from './pages/client/Devis';

// Admin
import Login         from './pages/admin/Login';
import AdminLayout   from './pages/admin/Layout';
import Dashboard     from './pages/admin/Dashboard';
import Orders        from './pages/admin/Orders';
import Products      from './pages/admin/Products';
import AdminStock    from './pages/admin/AdminStock';
import AdminContacts from './pages/admin/AdminContacts';
import AdminUsers    from './pages/admin/AdminUsers';
import AdminSettings from './pages/admin/AdminSettings';

// StockMed
import StockAuth      from './pages/stock/StockAuth';
import StockLayout    from './pages/stock/StockLayout';
import StockDashboard from './pages/stock/StockDashboard';
import StockProducts  from './pages/stock/StockProducts';
import StockMovements from './pages/stock/StockMovements';
import StockAlerts    from './pages/stock/StockAlerts';
import StockUpgrade   from './pages/stock/StockUpgrade';
import StockScanner   from './pages/stock/StockScanner';

const qc = new QueryClient({ defaultOptions:{ queries:{ retry:1, staleTime:30000 } } });

// ← Remplace par ton vrai numéro (sans + ni espaces)
const WHATSAPP_NUMBER = '212600000000';

function WhatsAppButton() {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}`}
      target="_blank"
      rel="noreferrer"
      aria-label="Contacter LOMED MEDICARE sur WhatsApp"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
        width: 54, height: 54, borderRadius: '50%',
        background: '#25D366',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 4px 16px rgba(37,211,102,${hov ? 0.55 : 0.35})`,
        textDecoration: 'none',
        transform: hov ? 'scale(1.1)' : 'scale(1)',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
    >
      <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, fill: '#fff' }}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
}

function ClientLayout({ lang, setLang, children }) {
  return (
    <div style={{ minHeight:'100vh', background:'#f8fafc' }}>
      <Navbar lang={lang} setLang={setLang}/>
      {children}
    </div>
  );
}

function StockGuard({ children }) {
  return localStorage.getItem('stockToken') ? children : <Navigate to="/stock" replace/>;
}

export default function App() {
  const [lang, setLang] = useState('fr');
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <CartProvider>
          <StockAuthProvider>
            <StockProvider>
              <BrowserRouter>
                <Routes>
                  {/* LOMED Client */}
                  <Route path="/"              element={<ClientLayout lang={lang} setLang={setLang}><Home         lang={lang}/></ClientLayout>}/>
                  <Route path="/catalogue"     element={<ClientLayout lang={lang} setLang={setLang}><Catalogue    lang={lang}/></ClientLayout>}/>
                  <Route path="/reservation"   element={<ClientLayout lang={lang} setLang={setLang}><Reservation  lang={lang}/></ClientLayout>}/>
                  <Route path="/about"         element={<ClientLayout lang={lang} setLang={setLang}><About        lang={lang}/></ClientLayout>}/>
                  <Route path="/contact"       element={<ClientLayout lang={lang} setLang={setLang}><Contact      lang={lang}/></ClientLayout>}/>
                  <Route path="/mes-commandes" element={<ClientLayout lang={lang} setLang={setLang}><MesCommandes lang={lang}/></ClientLayout>}/>
                  <Route path="/devis"         element={<ClientLayout lang={lang} setLang={setLang}><Devis        lang={lang}/></ClientLayout>}/>

                  {/* Admin */}
                  <Route path="/admin/login" element={<Login/>}/>
                  <Route path="/admin" element={<ProtectedRoute><AdminLayout/></ProtectedRoute>}>
                    <Route index element={<Navigate to="dashboard" replace/>}/>
                    <Route path="dashboard" element={<Dashboard/>}/>
                    <Route path="orders"    element={<Orders/>}/>
                    <Route path="products"  element={<Products/>}/>
                    <Route path="stock"     element={<AdminStock/>}/>
                    <Route path="contacts"  element={<AdminContacts/>}/>
                    <Route path="users"     element={<AdminUsers/>}/>
                    <Route path="settings"  element={<AdminSettings/>}/>
                  </Route>

                  {/* StockMed */}
                  <Route path="/stock" element={<StockAuth/>}/>
                  <Route path="/stock/*" element={<StockGuard><StockLayout/></StockGuard>}>
                    <Route path="dashboard"  element={<StockDashboard/>}/>
                    <Route path="products"   element={<StockProducts/>}/>
                    <Route path="movements"  element={<StockMovements/>}/>
                    <Route path="alerts"     element={<StockAlerts/>}/>
                    <Route path="upgrade"    element={<StockUpgrade/>}/>
                    <Route path="scanner"    element={<StockScanner/>}/>
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>
              </BrowserRouter>
              <Toaster position="bottom-right" toastOptions={{ style:{ fontSize:'13px', borderRadius:'10px' } }}/>
            </StockProvider>
          </StockAuthProvider>
        </CartProvider>
      </AuthProvider>

      {/* WhatsApp flottant — visible sur toutes les pages */}
      <WhatsAppButton />

    </QueryClientProvider>
  );
}